import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { NormalizedInterval } from 'date-fns'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'

export interface BooksReadByGenreTotalsResponse {
  books_read_by_genre_totals_summary_by_grade: SummaryData[]
  books_read_by_genre_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'student_count'; count: number },
    { category: 'total_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface MonthData {
  grade_position: number
  months: { month: string; fiction: number; nonfiction: number }[]
}

export interface BooksReadByGenreTotalsData {
  grade_position: number
  summary: {
    student_count: number
    total_count: number
    student_average: number
  }
  months: {
    fiction: number
    nonfiction: number
    month: string
  }[]
}

export async function booksReadByGenreTotals(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<BooksReadByGenreTotalsData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('books_read_by_genre_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<BooksReadByGenreTotalsResponse>(url, query)
  if (!data) return null

  const {
    books_read_by_genre_totals_summary_by_grade: summaryData,
    books_read_by_genre_totals_by_grade_by_month: monthData,
  } = data

  if (summaryData.length === 0 || monthData.length === 0) return null

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(summaryData: SummaryData[], monthData: MonthData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const student_count = summary.data.find((value) => value.category === 'student_count')?.count ?? 0
      const total_count = summary.data.find((value) => value.category === 'total_count')?.count ?? 0
      const student_average = summary.data.find((value) => value.category === 'student_average')?.average ?? 0
      const fullMonthData = fillMonths(gradeMonthData.months, period, {
        fiction: 0,
        nonfiction: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          student_count,
          total_count,
          student_average,
        },
        months: fullMonthData,
      }
    })
    .filter((data) => data !== null)
}
